// Copyright (c) Docugami, Inc. All rights reserved.

import { ReportViewMode } from "hooks/navigationService";
import { ProviderId } from "networking/StorageProviders";
import { WorkPathCategory, WorkPathType } from "networking/WorkPathApi";
import { Citation } from "store/chat/chatApi";
import { FeatureFlags } from "store/featureFlags/types";
import { AutomationId } from "utils/Automation";

export enum TelemetryEvent {
    AbstractTemplateApplied = 'AbstractTemplateApplied',
    AbstractTemplateAppliedWithLegacy = 'AbstractTemplateAppliedWithLegacy',

    AddFilesClicked = 'AddFilesClicked',
    AddFolderClicked = 'AddFolderClicked',

    APIKeyCreated = 'APIKeyCreated',
    APIKeyCopied = 'APIKeyCopied',

    CanvasSelectionModeEvent = 'CanvasSelectionModeEvent',

    ChangeDocSetCancelled = 'ChangeDocSetCancelled',
    ChangeDocSetClicked = 'ChangeDocSetClicked',
    ChangeDocSetConfirmed = 'ChangeDocSetConfirmed',
    ChangeDocSetFailed = 'ChangeDocSetFailed',

    // Chat events
    ChatCitationClicked = 'ChatCitationClicked',
    ChatFeedbackGiven = 'ChatFeedbackGiven',
    ChatInitializationTimeout = 'ChatInitializationError',
    ChatMessageSent = 'ChatMessageSent',
    ChatResponseTimeout = 'ChatResponseTimeout',
    ChatSuggestionClicked = 'ChatSuggestionClicked',

    ReassignDocToExistingDocSet = 'ReassignDocToExistingDocSet',
    ReassignDocToNewDocSet = 'ReassignDocToNew',
    RemoveDocFromDocSet = 'RemoveDocFromDocSet',

    ChooseDocumentStepSelectionChanged = 'ChooseDocumentStepSelectionChanged',

    DeleteWorkPath = 'DeletedWorkPath',

    DeleteFileCancelled = 'DeleteFileCancelled',
    DeleteFileClicked = 'DeleteFileClicked',
    DeleteFileConfirmed = 'DeleteFileConfirmed',

    DocSetSelected = 'DocSetSelected',
    DocSetRenameClicked = 'DocSetRenameClicked',
    DocSetRenameConfirmed = 'DocSetRenameConfirmed',
    DocSetRenameCancelled = 'DocSetRenameCancelled',
    DocSetAutoAssignClicked = 'DocSetAutoAssignClicked',
    DocSetAutoAssignConfirmed = 'DocSetAutoAssignConfirmed',
    DocSetAutoAssignCancelled = 'DocSetAutoAssignCancelled',
    DocSetDescriptionClicked = 'DocSetDescriptionClicked',
    DocSetDescriptionConfirmed = 'DocSetDescriptionConfirmed',
    DocSetDescriptionCancelled = 'DocSetDescriptionCancelled',
    DocSetDescriptionReverted = 'DocSetDescriptionReverted',
    DocSetDGMLv2Clicked = 'DocSetDGMLv2Clicked',
    DocSetDGMLv2Confirmed = 'DocSetDGMLv2Confirmed',
    DocSetDGMLv2Cancelled = 'DocSetDGMLv2Cancelled',
    QualityControlDescriptionClicked = 'QualityControlDescriptionClicked',
    QualityControlDescriptionConfirmed = 'QualityControlDescriptionConfirmed',
    QualityControlCancelled = 'QualityControlCancelled',
    QualityControlDescriptionReverted = 'QualityControlDescriptionReverted',
    QualityControlEnableStateChanged = 'QualityControlEnableStateChanged',

    DownloadFileClicked = 'DownloadFileClicked',
    DownloadFilesClicked = 'DownloadFilesClicked',
    DownloadXMLCommand = 'DownloadXMLCommand',
    DownloadDgmlxCommand = 'DownloadDgmlIxCommand',

    // Not used current...???
    DownloadReportAsDocx = 'DownloadReportAsDocx',
    DownloadSpreadsheetPreview = "DownloadSpreadsheetPreview",

    FeatureFlagToggle = 'FeatureFlagToggle',

    WebAppUserLoggedIn = 'WebAppUserLoggedIn',
    UserLoggedOut = 'UserLoggedOut',

    MainMenuEntryClicked = 'MainMenuEntryClicked',

    OrgSwitcherChangingOrg = 'OrgSwitcherChangingOrg',

    PageFilmThumbnailClicked = 'PageFilmThumbnailClicked',
    PageFilmNumberInputed = 'PageFilmNumberInputed',

    RenamedWorkPath = 'RenamedWorkPath',
    RenameWorkPathDialogVisibility = 'OpenedRenameWorkPathDialog',

    ReportFileNameClicked = 'ReportFileNameClicked',
    ReportSummaryPublished = 'ReportSummaryPublished',

    SearchOpened = 'SearchOpened',
    SearchNavigateToResult = 'SearchNavigateToResult',
    SettingsPanelOpened = 'SettingsPanelOpened',
    SettingsPanelHiddenButtonToggled = 'SettingsPanelHiddenButtonToggled',
    CreateWorkPath = 'CreateWorkPath',

    UserbackOpened = 'UserbackOpened',
    UserbackClosed = 'UserbackClosed',
    UserbackSending = 'UserbackSending',
    UserbackSent = 'UserbackSent',

    ChunkResized = 'ChunkResized',
    ChunkCreated = 'ChunkCreated',
    ChunkSelected = 'ChunkSelected',
    SelectionModeChanged = "SelectionModeChanged",
    ConfirmEntryBinding = "ConfirmEntryBinding",
    LockAllEntryBindings = "LockAllEntryBindings",
    LockAllDocumentBindings = "LockAllDocumentBindings",
    RemoveEntries = 'RemoveEntries',
    AddZeroShotEntryButtonClicked = 'AddZeroShotEntryButtonClicked',
    ZeroShotEntryAdded = 'ZeroShotEntryAdded',
    TagEditorEdit = "TagEditorEdit",
    TagEditorEditCancelled = "TagEditorEditCancelled",
    TagEditorSave = "TagEditorSave",
    TagEditorTagSelected = "TagEditorTagSelected",

    InstalledSampleSet = "InstalledSampleSet",
    FirstArrivalUserUploadCompleted = "FirstArrivalUserUploadCompleted",
    StorageProviderOpened = "StorageProviderOpened",
    StorageProviderItemsSelected = "StorageProviderItemsSelected",
    UserFilesDropped = "UserFilesDropped",

    WorkPathNameChanged = 'WorkPathNameChanged',
    WorkPathPublished = 'WorkPathPublished',
    WorkPathCloneProjectSelected = 'WorkPathCloneProjectSelected',
    WorkPathCreationAdvancedPanelOpened = 'WorkPathCreationAdvancedPanelOpened',
    WorkPathCreationAdvancedPanelClosed = 'WorkPathCreationAdvancedPanelClosed',

    WorkPathViewerItemClicked = 'WorkPathViewerItemClicked',
    DocumentPreviewVisibilityChanged = "DocumentPreviewVisibilityChanged"
}

export interface BaseTelemetryEventSchema<Event> {
    eventName: Event;
}

export interface ConfirmEntryBindingSchema extends BaseTelemetryEventSchema<TelemetryEvent.ConfirmEntryBinding> {
    entryId: string;
    instanceId: string;
    documentId: string;
    reportId: string;
}

export interface AbstractTemplateAppliedSchema extends BaseTelemetryEventSchema<TelemetryEvent.AbstractTemplateApplied> { }

export interface AbstractTemplateAppliedWithLegacySchema extends BaseTelemetryEventSchema<TelemetryEvent.AbstractTemplateAppliedWithLegacy> { }

export interface AddFilesClickedSchema extends BaseTelemetryEventSchema<TelemetryEvent.AddFilesClicked> { }

export interface AddFolderClickedSchema extends BaseTelemetryEventSchema<TelemetryEvent.AddFolderClicked> { }

export interface APIKeyCreatedSchema extends BaseTelemetryEventSchema<TelemetryEvent.APIKeyCreated> {
    user: string;
    workspace: string;
    tokenName?: string;
}

export interface APIKeyCopiedSchema extends BaseTelemetryEventSchema<TelemetryEvent.APIKeyCopied> {
    user: string;
    workspace: string;
}

export interface CanvasSelectionModeEventSchema extends BaseTelemetryEventSchema<TelemetryEvent.CanvasSelectionModeEvent> {
    mode: string;
}

export interface ChangeDocSetCancelledSchema extends BaseTelemetryEventSchema<TelemetryEvent.ChangeDocSetCancelled> { }

export interface ChangeDocSetClickedSchema extends BaseTelemetryEventSchema<TelemetryEvent.ChangeDocSetClicked> { }

export interface ChangeDocSetConfirmedSchema extends BaseTelemetryEventSchema<TelemetryEvent.ChangeDocSetConfirmed> { }

export interface ChangeDocSetFailedSchema extends BaseTelemetryEventSchema<TelemetryEvent.ChangeDocSetFailed> {
    error?: any;
}

export interface ReassignDocToExistingDocSetSchema extends BaseTelemetryEventSchema<TelemetryEvent.ReassignDocToExistingDocSet> {
    docSetId: string;
    itemCount: number;
    items: string[];
}

export interface ReassignDocToNewDocSetSchema extends BaseTelemetryEventSchema<TelemetryEvent.ReassignDocToNewDocSet> {
    docSetName: string;
    itemCount: number;
    items: string[];
}

export interface RemoveDocFromDocSetSchema extends BaseTelemetryEventSchema<TelemetryEvent.RemoveDocFromDocSet> {
    itemCount: number;
    items: string[];
}

export interface ChooseDocumentStepSelectionChangedSchema extends BaseTelemetryEventSchema<TelemetryEvent.ChooseDocumentStepSelectionChanged> {
    folderId: string;
}

export interface DeleteWorkPathSchema extends BaseTelemetryEventSchema<TelemetryEvent.DeleteWorkPath> {
    workPathId: string;
}
export interface DeleteFileCancelledSchema extends BaseTelemetryEventSchema<TelemetryEvent.DeleteFileCancelled> { }

export interface DeleteFileClickedSchema extends BaseTelemetryEventSchema<TelemetryEvent.DeleteFileClicked> { }

export interface DeleteFileConfirmedSchema extends BaseTelemetryEventSchema<TelemetryEvent.DeleteFileConfirmed> { }

export interface DocSetSelectedSchema extends BaseTelemetryEventSchema<TelemetryEvent.DocSetSelected> {
    selectedDocSetId: string;
    isDocSetValid: boolean;
}

export interface DocSetRenameClickedSchema extends BaseTelemetryEventSchema<TelemetryEvent.DocSetRenameClicked> {
    docSetId: string;
}

export interface DocSetRenameConfirmedSchema extends BaseTelemetryEventSchema<TelemetryEvent.DocSetRenameConfirmed> {
    docSetId: string;
}

export interface DocSetRenameCancelledSchema extends BaseTelemetryEventSchema<TelemetryEvent.DocSetRenameCancelled> {
    docSetId: string;
}

export interface DocSetAutoAssignClickedSchema extends BaseTelemetryEventSchema<TelemetryEvent.DocSetAutoAssignClicked> {
    unassignedCount: number;
}

export interface DocSetAutoAssignConfirmedSchema extends BaseTelemetryEventSchema<TelemetryEvent.DocSetAutoAssignConfirmed> {
    unassignedCount: number;
}

export interface DocSetDescriptionClickedSchema extends BaseTelemetryEventSchema<TelemetryEvent.DocSetDescriptionClicked> {
    docSetId: string;
}

export interface DocSetDescriptionConfirmedSchema extends BaseTelemetryEventSchema<TelemetryEvent.DocSetDescriptionConfirmed> {
    docSetId: string;
}

export interface DocSetDescriptionCancelledSchema extends BaseTelemetryEventSchema<TelemetryEvent.DocSetDescriptionCancelled> {
    docSetId: string;
}

export interface DocSetDescriptionRevertedSchema extends BaseTelemetryEventSchema<TelemetryEvent.DocSetDescriptionReverted> {
    docSetId: string;
}

export interface DocSetDGMLv2ClickedSchema extends BaseTelemetryEventSchema<TelemetryEvent.DocSetDGMLv2Clicked> {
    docSetId: string;
}

export interface DocSetDGMLv2ConfirmedSchema extends BaseTelemetryEventSchema<TelemetryEvent.DocSetDGMLv2Confirmed> {
    docSetId: string;
    dgmlv2Enabled: boolean;
}

export interface DocSetDGMLv2CancelledSchema extends BaseTelemetryEventSchema<TelemetryEvent.DocSetDGMLv2Cancelled> {
    docSetId: string;
}

export interface QualityControlDescriptionConfirmedSchema extends BaseTelemetryEventSchema<TelemetryEvent.QualityControlDescriptionConfirmed> {
    docSetId: string;
    labelId: string;
}

export interface QualityControlEnableStateChangedSchema extends BaseTelemetryEventSchema<TelemetryEvent.QualityControlEnableStateChanged> {
    docSetId: string;
    labelId: string;
}

export interface QualityControlDescriptionRevertedSchema extends BaseTelemetryEventSchema<TelemetryEvent.QualityControlDescriptionReverted> {
    docSetId: string;
    labelId: string;
}

export interface QualityControlCancelledSchema extends BaseTelemetryEventSchema<TelemetryEvent.QualityControlCancelled> {
    docSetId: string;
    labelId: string;
}

export interface QualityControlDescriptionClickedSchema extends BaseTelemetryEventSchema<TelemetryEvent.QualityControlDescriptionClicked> {
    docSetId: string;
    labelId: string;
}

export interface DocSetAutoAssignCancelledSchema extends BaseTelemetryEventSchema<TelemetryEvent.DocSetAutoAssignCancelled> { }

export interface DownloadFileClickedSchema extends BaseTelemetryEventSchema<TelemetryEvent.DownloadFileClicked> { }

export interface DownloadFilesClickedSchema extends BaseTelemetryEventSchema<TelemetryEvent.DownloadFilesClicked> { }

export interface DownloadXMLCommandSchema extends BaseTelemetryEventSchema<TelemetryEvent.DownloadXMLCommand> { }

export interface DownloadDgmlxCommandSchema extends BaseTelemetryEventSchema<TelemetryEvent.DownloadDgmlxCommand> { }

export interface DownloadReportAsDocxSchema extends BaseTelemetryEventSchema<TelemetryEvent.DownloadReportAsDocx> { }

export interface DownloadSpreadsheetPreviewSchema extends BaseTelemetryEventSchema<TelemetryEvent.DownloadSpreadsheetPreview> { }

export interface FeatureFlagToggleSchema extends BaseTelemetryEventSchema<TelemetryEvent.FeatureFlagToggle> {
    featureFlag: FeatureFlags,
    enabled: boolean;
}

export interface WebAppUserLoggedInSchema extends BaseTelemetryEventSchema<TelemetryEvent.WebAppUserLoggedIn> { }

export interface UserLoggedOutSchema extends BaseTelemetryEventSchema<TelemetryEvent.UserLoggedOut> { }

export interface MainMenuEntryClickedSchema extends BaseTelemetryEventSchema<TelemetryEvent.MainMenuEntryClicked> {
    name: string;
}

export interface OrgSwitcherChangingOrgSchema extends BaseTelemetryEventSchema<TelemetryEvent.OrgSwitcherChangingOrg> {
    previousOrgId: string;
    nextOrgId: string | number;
}

export interface PageFilmThumbnailClickedSchema extends BaseTelemetryEventSchema<TelemetryEvent.PageFilmThumbnailClicked> {
    pageNumber: number;
}

export interface PageFilmNumberInputedSchema extends BaseTelemetryEventSchema<TelemetryEvent.PageFilmNumberInputed> {
    pageNumber: number;
}

export interface RenamedWorkPathSchema extends BaseTelemetryEventSchema<TelemetryEvent.RenamedWorkPath> {
    workPathId: string;
    previousName: string;
    name: string;
}

export interface RenameWorkPathDialogVisibilitySchema extends BaseTelemetryEventSchema<TelemetryEvent.RenameWorkPathDialogVisibility> {
    isVisible: boolean;
}

export interface ReportFileNameClickedSchema extends BaseTelemetryEventSchema<TelemetryEvent.ReportFileNameClicked> {
    folderId: string;
}

export interface ReportSummaryPublishedSchema extends BaseTelemetryEventSchema<TelemetryEvent.ReportSummaryPublished> { }

export interface SearchOpenedSchema extends BaseTelemetryEventSchema<TelemetryEvent.SearchOpened> { }

export interface SearchNavigateToResultSchema extends BaseTelemetryEventSchema<TelemetryEvent.SearchNavigateToResult> {
    resultIndex: number;
}

export interface SettingsPanelOpenedSchema extends BaseTelemetryEventSchema<TelemetryEvent.SettingsPanelOpened> { }

export interface SettingsPanelHiddenButtonToggledSchema extends BaseTelemetryEventSchema<TelemetryEvent.SettingsPanelHiddenButtonToggled> { }

export interface CreateWorkPathSchema extends BaseTelemetryEventSchema<TelemetryEvent.CreateWorkPath> {
    workPathType: WorkPathType;
}

export interface UserbackOpenedSchema extends BaseTelemetryEventSchema<TelemetryEvent.UserbackOpened> {
    userbackId: string;
}

export interface UserbackClosedSchema extends BaseTelemetryEventSchema<TelemetryEvent.UserbackClosed> {
    userbackId: string;
}

export interface UserbackSendingSchema extends BaseTelemetryEventSchema<TelemetryEvent.UserbackSending> {
    userbackId: string;
}

export interface UserbackSentSchema extends BaseTelemetryEventSchema<TelemetryEvent.UserbackSent> {
    userbackId: string;
}

export interface ChunkResizedSchema extends BaseTelemetryEventSchema<TelemetryEvent.ChunkResized> {
    originalChunkInstanceId: string;
    originalSpanId: string;
    newChunkInstanceId: string;
    newSpanId: string;
}

export interface ChunkCreatedSchema extends BaseTelemetryEventSchema<TelemetryEvent.ChunkCreated> {
    spanId: string;
}

export interface ChunkSelectedSchema extends BaseTelemetryEventSchema<TelemetryEvent.ChunkSelected> {
    spanId: string;
}

export interface SelectionModeChangedSchema extends BaseTelemetryEventSchema<TelemetryEvent.SelectionModeChanged> {
    isActive: boolean;
}

export interface LockAllEntryBindingsSchema extends BaseTelemetryEventSchema<TelemetryEvent.LockAllEntryBindings> {
    reportId: string;
    entryId: string;
    documentIds: string[];
}

export interface LockAllDocumentBindingsSchema extends BaseTelemetryEventSchema<TelemetryEvent.LockAllDocumentBindings> {
    reportId: string;
    folderId: string;
}

export interface RemoveEntriesSchema extends BaseTelemetryEventSchema<TelemetryEvent.RemoveEntries> {
    reportId: string;
    isRemoveAll?: boolean;
    entryIds: string[];
}

export interface AddZeroShotEntryButtonClickedSchema extends BaseTelemetryEventSchema<TelemetryEvent.AddZeroShotEntryButtonClicked> {
    reportId: string;
}

export interface ZeroShotEntryAddedSchema extends BaseTelemetryEventSchema<TelemetryEvent.ZeroShotEntryAdded> {
    reportId: string;
    label: string;
}

export interface TagEditorEditSchema extends BaseTelemetryEventSchema<TelemetryEvent.TagEditorEdit> { }

export interface TagEditorEditCancelledSchema extends BaseTelemetryEventSchema<TelemetryEvent.TagEditorEditCancelled> { }

export interface TagEditorSaveSchema extends BaseTelemetryEventSchema<TelemetryEvent.TagEditorSave> { }

export interface TagEditorTagSelectedSchema extends BaseTelemetryEventSchema<TelemetryEvent.TagEditorTagSelected> {
    label: string;
    type: string;
}

export interface WorkPathPublishedSchema extends BaseTelemetryEventSchema<TelemetryEvent.WorkPathPublished> { }

export interface WorkPathCloneProjectSelectedSchema extends BaseTelemetryEventSchema<TelemetryEvent.WorkPathCloneProjectSelected> {
    clonedWorkPathId: string;
}

export interface WorkPathCreationAdvancedPanelOpenedSchema extends BaseTelemetryEventSchema<TelemetryEvent.WorkPathCreationAdvancedPanelOpened> { }

export interface WorkPathCreationAdvancedPanelClosedSchema extends BaseTelemetryEventSchema<TelemetryEvent.WorkPathCreationAdvancedPanelClosed> { }

export interface WorkPathViewerItemClickedSchema extends BaseTelemetryEventSchema<TelemetryEvent.WorkPathViewerItemClicked> { workPathId: string }

export interface InstalledSampleSetSchema extends BaseTelemetryEventSchema<TelemetryEvent.InstalledSampleSet> { sampleId: string }

export interface FirstArrivalUserUploadCompletedSchema extends BaseTelemetryEventSchema<TelemetryEvent.FirstArrivalUserUploadCompleted> { numOfFiles: number }

export interface StorageProviderOpenedSchema extends BaseTelemetryEventSchema<TelemetryEvent.StorageProviderOpened> { providerId: ProviderId }

export interface StorageProviderItemsSelectedSchema extends BaseTelemetryEventSchema<TelemetryEvent.StorageProviderItemsSelected> {
    providerId: ProviderId,
    numOfItems: number
}

export interface UserFilesDroppedSchema extends BaseTelemetryEventSchema<TelemetryEvent.UserFilesDropped> { numOfFiles: number }

export interface DocumentPreviewVisibilityChangedSchema extends BaseTelemetryEventSchema<TelemetryEvent.DocumentPreviewVisibilityChanged> {
    visibility: boolean;
    folderId?: string;
    clusterId?: string;
}

export interface ChatCitationClickedSchema extends BaseTelemetryEventSchema<TelemetryEvent.ChatCitationClicked> {
    citation: Citation;
}

export interface ChatFeedbackGivenSchema extends BaseTelemetryEventSchema<TelemetryEvent.ChatFeedbackGiven> {
    messageId: string;
    feedback: boolean;
    comment?: string;
}

export interface ChatInitializationTimeoutSchema extends BaseTelemetryEventSchema<TelemetryEvent.ChatInitializationTimeout> {
    error: any;
    durationMs?: number;
    statusCode?: number | string;
}

export interface ChatResponseTimeoutSchema extends BaseTelemetryEventSchema<TelemetryEvent.ChatResponseTimeout> {
    messageId: string;
}

export interface ChatMessageSentSchema extends BaseTelemetryEventSchema<TelemetryEvent.ChatMessageSent> {
    messageContent: string;
}

export interface ChatSuggestionClickedSchema extends BaseTelemetryEventSchema<TelemetryEvent.ChatSuggestionClicked> {
    suggestionId: string;
    suggestionContent: string;
}

export enum TelemetryExceptions {
    RequestDisabled = 'RequestDisabled',
    SignalRConnectionFailed = 'SignalRConnectionFailed',
    WorkPathConstructionFailed = 'WorkPathConstructionFailed',
    BoxPickerLoadFailed = 'BoxPickerLoadFailed',
    DropboxPickerLoadFailed = 'DropboxPickerLoadFailed'
}

export enum TelemetryCategory {
    Network = 'Network'
}

export enum TelemetryPageView {
    AbstractViewer = 'AbstractViewer',
    Account = 'Account',
    Chat = 'Chat',
    CreateWorkspaceInfo = 'CreateWorkspaceInfo',
    CreatingWorkspace = 'CreatingWorkspace',
    CreateWorkspaceWaitlisted = 'CreateWorkspaceWaitlisted',
    CreateDocsSubHome = 'CreateDocsSubHome',
    Dashboard = 'Dashboard',
    DocExplorer = 'DocExplorer',
    DocViewer = 'DocViewer',
    EmailVerification = 'EmailVerification',
    FirstArrival = 'FirstArrival',
    Home = 'Home',
    NotFound = 'NotFound',
    ReportTable = 'ReportTable',
    ScheduledMaintenance = 'ScheduledMaintenance',
    SessionExpired = 'SessionExpired',
    Tiers = 'Tiers',
    TrialLanding = 'TrialLanding',
    UnderstandDocsSubHome = 'UnderstandDocsSubHome',
    WorkflowSubHome = 'WorkflowSubHome',
    WorkPath = 'WorkPath',
}

export function getClickEventIdForAutomationId(automationId: AutomationId): string {
    return `${automationId}_Clicked`
}

export function getPageViewForWorkPathCategory(workPathCategory: WorkPathCategory | undefined): TelemetryPageView {
    switch (workPathCategory) {
        case WorkPathCategory.Create:
            return TelemetryPageView.CreateDocsSubHome;
        case WorkPathCategory.Understand:
            return TelemetryPageView.UnderstandDocsSubHome;
        case WorkPathCategory.Workflow:
            return TelemetryPageView.WorkflowSubHome;
        default:
            return TelemetryPageView.Home
    }
}

export function getPageViewForReportViewMode(mode: ReportViewMode): TelemetryPageView {
    switch (mode) {
        case ReportViewMode.Abstract:
            return TelemetryPageView.AbstractViewer;
        case ReportViewMode.Document:
            return TelemetryPageView.DocViewer;
        case ReportViewMode.Table:
            return TelemetryPageView.ReportTable;
        case ReportViewMode.Chat:
            return TelemetryPageView.Chat;
        default:
            throw new Error("Unknown report view mode without corresponding page view.");
    }
}
